/** The audio engine. */

import type { Rhythm } from '@tunasong/models'
import { logger, meterBottom, meterTop, shortUuid } from '@tunasong/models'
import { EasyScore } from '@tunasong/music-lib'
import type { NoteEvent } from '@tunasong/music-lib'
import type { UserConfigService } from '@tunasong/redux'
import { isSafari } from '@tunasong/ui-lib'
import type { IAudioContext } from 'standardized-audio-context'
import invariant from 'tiny-invariant'
import { Ticks, Context as ToneContext, getContext, getDraw, getTransport, setContext, start } from 'tone'
import type { TransportTime } from 'tone/build/esm/core/type/Units.js'
import { getDevices } from '../devices.js'
import type { MidiNoteEvent } from '../events/midi-event.js'
import { noteEventToMidi } from '../events/note-event-to-midi.js'
import type { AudioInstrument } from '../instruments/index.js'
import { MidiControl } from '../midi/midi.js'
import { ChannelController } from './channel.js'
import { createAudioContext } from './context.js'
import { Mixer } from './mixer.js'
import { outputTimeAt } from './playback-clock.js'
import { scheduledNoteToMidi, toTransportTimes } from './scheduled-note.js'
import type { ScheduledNote } from './scheduled-note.js'
import { InputSource } from './sources/input-source.js'

const DEFAULT_INPUT_STORAGE_KEY = 'tunasong.audio.mic'
const DEFAULT_OUTPUT_STORAGE_KEY = 'tunasong.audio.output'

/**
 * How far ahead of the transport's lookahead the first scheduled note is placed. The Tone
 * transport invokes a callback up to `clockUpdateInterval` (100 ms by default) before the audio
 * deadline; a note scheduled inside that window fires late.
 */
export const SCHEDULE_LEAD_MS = 120

declare global {
  var TONE_SILENCE_LOGGING: boolean
}

/** The AudioEngine is a thin layer on top of Tone.JS  for now */
interface IAudioEngine {
  /** The number of seconds between each update interval. This will determine the latency between an event scheduled and. @default 0.1 */
  clockUpdateInterval?: number
  configService: UserConfigService
}

/**
 * Events:
 * - 'devicechange' - when the audio devices change.
 */
export class AudioEngine extends EventTarget {
  public midi: MidiControl | null
  private currentMixer: Mixer | null = null
  private currentAudioContext: IAudioContext | null = null
  private clockUpdateInterval?: number
  private instruments: Record<string, AudioInstrument | undefined> = {}
  /**
   * What each named schedule put on the transport, so `cancelScheduled` can take it back: the transport
   * event ids, and the notes sounding now with the noteoff that ends each (melody toolkit plan M2).
   */
  private scheduleGroups = new Map<
    string,
    { eventIds: number[]; sounding: Map<number, { off: MidiNoteEvent; instrument: AudioInstrument }> }
  >()
  private nextSoundingKey = 0

  private inputSources: InputSource[] = []
  private inputDefault: InputSource | null = null
  private inputDefaultController: ChannelController | null = null
  private currentDefaultInputDevice: MediaDeviceInfo | null = null
  private currentDefaultOutputDevice: MediaDeviceInfo | null = null

  /**
   * The constructor must stay cheap: it runs at app bootstrap on the
   * LCP-critical path. The audio stack (AudioContext, Mixer, Tone context)
   * is initialized lazily on first audio use - @see initializeAudio and
   * https://github.com/tunasong/tunasong/issues/1542
   */
  constructor({ clockUpdateInterval, configService }: IAudioEngine) {
    super()
    logger.debug('Creating new audio engine', shortUuid())
    this.clockUpdateInterval = clockUpdateInterval

    const lsDevice = localStorage.getItem(DEFAULT_INPUT_STORAGE_KEY)
    this.currentDefaultInputDevice = lsDevice ? (JSON.parse(lsDevice) as MediaDeviceInfo) : null

    const outputDevice = localStorage.getItem(DEFAULT_OUTPUT_STORAGE_KEY)
    this.currentDefaultOutputDevice = outputDevice ? (JSON.parse(outputDevice) as MediaDeviceInfo) : null

    /** MIDI support */
    this.midi = isSafari() ? null : new MidiControl({ configService })
  }

  get initialized() {
    return this.currentAudioContext !== null
  }

  get mixer(): Mixer {
    return this.initializeAudio().mixer
  }

  get transport() {
    this.initializeAudio()
    return getTransport()
  }

  get draw() {
    this.initializeAudio()
    return getDraw()
  }

  get started() {
    return this.currentAudioContext?.state === 'running'
  }

  get defaultInput() {
    return this.inputDefaultController
  }

  get availableInputSources() {
    return this.inputSources
  }

  get bpm() {
    return this.transport.bpm.value
  }

  /** Set bpm at the current position. */
  set bpm(bpm: number) {
    this.transport.bpm.value = bpm
  }

  /** Set Rhythm */
  set rhythm(rhythm: Rhythm) {
    const { tempo, meter } = rhythm
    if (tempo) {
      this.transport.bpm.value = tempo
    }
    if (meter) {
      /** Tone wants the time signature as written: `[top, bottom]`. It reduces the pair with
       * `top / bottom * 4`, so transposing the two silently changes the length of a bar. */
      this.transport.timeSignature = [meterTop(meter), meterBottom(meter)]
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get defaultInputDevice() {
    return this.currentDefaultInputDevice
  }

  set defaultInputDevice(inputDevice: MediaDeviceInfo | null) {
    localStorage.setItem(DEFAULT_INPUT_STORAGE_KEY, JSON.stringify(inputDevice))
    this.currentDefaultInputDevice = inputDevice
    this.setupDevices()
    this.dispatchEvent(new Event('devicechange'))
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get defaultOutputDevice() {
    return this.currentDefaultOutputDevice
  }

  set defaultOutputDevice(outputDevice: MediaDeviceInfo | null) {
    localStorage.setItem(DEFAULT_OUTPUT_STORAGE_KEY, JSON.stringify(outputDevice))
    this.currentDefaultOutputDevice = outputDevice
    this.dispatchEvent(new Event('devicechange'))
  }

  /** Simple default routing */
  routeDefaultInput = (source: InputSource) => {
    /** We create an input channel for the default input, and route it to the recording bus */
    if (!source) {
      return
    }
    if (this.inputDefaultController) {
      this.inputDefaultController.stop()
      this.inputDefaultController.disconnect()
      this.mixer.removeChannel(this.inputDefaultController.id)
    }
    this.inputDefaultController = this.mixer.addChannel(source, 'record')
  }

  start = async () => {
    if (this.started) {
      return
    }

    this.initializeAudio()
    await start()
  }

  stop = async () => {
    if (!this.currentAudioContext) {
      /** The audio stack was never initialized - nothing to stop */
      return
    }
    Object.keys(this.instruments).forEach(i => this.currentMixer?.removeChannel(i))
    this.transport.stop()
    void this.currentAudioContext.suspend()
  }

  play(notes: NoteEvent[], instrumentName?: string) {
    for (const note of notes) {
      this.scheduleNote({ note, instrumentName })
    }
  }

  playMidiEvent(event: MidiNoteEvent, instrumentName?: string) {
    /** Schedule the noteon and noteoff events */
    const at = '+1i'

    this.scheduleMidiEvents({
      events: [{ event, at }],
      instrumentName,
    })
  }

  /**
   * Play a sequence of event, where each event is played for `duration`, one after the other. The
   * times are relative to this call, so a passage must be ONE sequence: two calls both start now.
   *
   * A rest takes its duration and sounds nothing. A tied note (`tie`) is held on into the next
   * event rather than struck again - a note held over a barline in a score is two tied events.
   */
  playSequence(noteSequence: NoteEvent[], instrumentName?: string) {
    /** Schedule the noteon and noteoff events */
    let ticks = 1
    /** Where the note being held over a tie started, or null when nothing is held. */
    let heldFrom: number | null = null

    for (const [index, currentNoteEvent] of noteSequence.entries()) {
      const eventTicks = Ticks(currentNoteEvent.duration).toTicks()
      const startTicks: number = heldFrom ?? ticks
      const endTicks = ticks + eventTicks
      ticks += eventTicks

      if (currentNoteEvent.rest) {
        heldFrom = null
        continue
      }
      /**
       * A tie joins two notes of the same pitch. Into a rest, a different note or nothing at all,
       * it is ignored and the note simply ends - it must never lend its start to another pitch.
       */
      if (EasyScore.continuesTie(currentNoteEvent, noteSequence[index + 1])) {
        heldFrom = startTicks
        continue
      }
      heldFrom = null
      const start = `+${startTicks}i`
      const end = `+${endTicks}i`

      const events = noteEventToMidi(currentNoteEvent)
      this.scheduleMidiEvents({
        events: [
          { event: events[0], at: start },
          { event: events[1], at: end },
        ],
        instrumentName,
      })
    }
  }

  /**
   * Play notes at absolute times. Unlike `playSequence`, which walks by duration, each note
   * carries its own offset in ms - which is what makes a melody's `offsetMs` (its feel) audible.
   *
   * Returns the AudioContext time at which a note with `timeMs === 0` sounds - the origin of the
   * caller's timeline - or null when there is nothing to play. A play head belongs on THAT, not on
   * `performance.now()`: the audio is displaced from the moment of the call by `SCHEDULE_LEAD_MS`
   * AND by the Tone context's lookAhead, and a play head that knows about neither runs several
   * hundred ms ahead of the sound. @see playback-clock.ts
   */
  playScheduled(notes: ScheduledNote[], instrumentName?: string, group?: string): number | null {
    if (notes.length === 0) {
      return null
    }
    /** Read from where each `+X` below is resolved, never recomputed from constants. @see scheduleOrigin */
    const origin = this.scheduleOrigin()

    const record = group ? this.groupRecord(group) : undefined
    notes.forEach(note => {
      const [on, off] = scheduledNoteToMidi(note)
      const { onAt, offAt } = toTransportTimes(note, SCHEDULE_LEAD_MS)
      /** Fixed now, so this note's on and off find the same entry whenever they fire */
      const key = this.nextSoundingKey++
      const ids = this.scheduleMidiEvents({
        events: [
          { event: on, at: onAt },
          { event: off, at: offAt },
        ],
        instrumentName,
        onFire: record
          ? (event, instrument) => {
              /** A note sounds from its noteon until its noteoff fires */
              if (event === on) {
                record.sounding.set(key, { off, instrument })
                return
              }
              record.sounding.delete(key)
            }
          : undefined,
      })
      record?.eventIds.push(...ids)
    })

    return origin
  }

  /**
   * The AudioContext time at which `timeMs === 0` would sound if `playScheduled` were called now. A loop
   * places its next pass against it, so the pass lands on the bar line rather than a lead late.
   *
   * Initialized BEFORE reading the clock: `initializeAudio` REPLACES the global Tone context, so a `now()`
   * taken first belongs to a context that is about to be disposed. `now()` is `currentTime + lookAhead`,
   * which is what Tone resolves each `+X` against.
   */
  scheduleOrigin(): number {
    this.initializeAudio()
    return getContext().now() + SCHEDULE_LEAD_MS / 1000
  }

  /**
   * Take back what `playScheduled` put on the transport under `group`: its events that have not fired
   * are cleared, and its notes sounding now are released. Other playback is untouched. A group that is
   * not scheduled is a no-op.
   */
  cancelScheduled(group: string) {
    const record = this.scheduleGroups.get(group)
    if (!record) {
      return
    }
    this.scheduleGroups.delete(group)
    const transport = this.transport
    for (const id of record.eventIds) {
      transport.clear(id)
    }
    const now = getContext().now()
    for (const { off, instrument } of record.sounding.values()) {
      instrument.onEvent(off, now)
    }
  }

  /**
   * The audio clock, in AudioContext seconds, as close to what is being HEARD as the platform
   * allows. Pair it with the start `playScheduled` returns to run a play head on the audio's own
   * clock. @see playback-clock.ts
   */
  outputTime(): number {
    return outputTimeAt(this.initializeAudio().audioContext, performance.now())
  }

  /** @todo Event routing */
  /** @todo Channel routing */

  /** @todo route instruments to the correct track. Currently we create a new track for each instrument */
  addInstrument(name: string, instrument: AudioInstrument) {
    const { audioContext, mixer } = this.initializeAudio()

    const id = shortUuid()
    this.instruments[id] = instrument
    const source = audioContext.createGain()
    /** @todo ToneJS requires InputNode which for some reason is not compatible with IAudioNode */
    instrument.connect(source as never)
    mixer.addChannel(
      {
        id,
        name,
        inputNode: source,
        type: 'synth',
      },
      'instrument'
    )
    return id
  }

  removeInstrument(id: string) {
    invariant(this.currentMixer, 'Mixer not initialized')

    this.currentMixer.removeChannel(id)
    const instrument = this.instruments[id]
    if (!instrument) {
      throw new Error(`Unknown instrument ID: ${id}`)
    }
    instrument.disconnect()
    delete this.instruments[id]
  }

  /**
   * Lazily initialize the audio stack. Expensive (~0.4s main-thread): creates
   * the AudioContext, the Mixer bus graph, and the Tone context (which builds
   * Destination/Transport/Draw). Must never run on the initial render path -
   * only from actual audio use (ideally a user gesture, since the AudioContext
   * starts suspended without one). @see https://github.com/tunasong/tunasong/issues/1542
   */
  private initializeAudio = () => {
    if (this.currentAudioContext && this.currentMixer) {
      return { audioContext: this.currentAudioContext, mixer: this.currentMixer }
    }

    /** Remove existing ToneJS context to avoid having clocks eating CPU - @see https://github.com/tunasong/tunasong/issues/105 */
    const oldContext = getContext()
    oldContext.dispose()
    invariant(oldContext.disposed === true, 'Old context not disposed')

    const audioContext = createAudioContext()
    const mixer = new Mixer(audioContext)
    this.currentAudioContext = audioContext
    this.currentMixer = mixer

    const toneContext = new ToneContext({
      context: audioContext as never,
      clockSource: 'worker',
      updateInterval: this.clockUpdateInterval,
    })
    setContext(toneContext)

    // Set up devices when the audio context is running
    audioContext.addEventListener('statechange', () => {
      if (audioContext.state === 'running') {
        this.setupDevices()
      }
    })

    /** Device changes */
    /** @todo do we need to remove this on destroy? */
    navigator.mediaDevices?.addEventListener('devicechange', this.setupDevices)
    /** Permission changes to the microphone to ensure we pick that up*/
    if (navigator?.permissions?.query) {
      void navigator.permissions.query({ name: 'microphone' }).then(permissionStatus => {
        permissionStatus.addEventListener('change', () => {
          if (permissionStatus.state === 'granted') {
            this.setupDevices()
          }
        })
      })
    }

    this.dispatchEvent(new Event('initialized'))

    return { audioContext, mixer }
  }

  private scheduleNote = ({ note, instrumentName }: { note: NoteEvent; instrumentName?: string }) => {
    const start = '+1i'
    /** @todo this means that the duration will be duration - 1 tick */
    const end = `+${note.duration}i`
    const events = noteEventToMidi(note)
    /** Schedule the noteon and noteoff events */
    this.scheduleMidiEvents({
      events: [
        { event: events[0], at: start },
        { event: events[1], at: end },
      ],
      instrumentName,
    })
  }

  /** A schedule group's record, created on first use */
  private groupRecord = (group: string) => {
    const existing = this.scheduleGroups.get(group)
    if (existing) {
      return existing
    }
    const created = {
      eventIds: [] as number[],
      sounding: new Map<number, { off: MidiNoteEvent; instrument: AudioInstrument }>(),
    }
    this.scheduleGroups.set(group, created)
    return created
  }

  /** Returns the transport event ids, for `cancelScheduled`. `onFire` hears each event as it fires */
  private scheduleMidiEvents = ({
    events,
    instrumentName,
    onFire,
  }: {
    events: { event: MidiNoteEvent; at: TransportTime }[]
    instrumentName?: string
    onFire?: (event: MidiNoteEvent, instrument: AudioInstrument) => void
  }): number[] => {
    const instrumentId = instrumentName || Object.keys(this.instruments)[0]
    if (!instrumentId) {
      logger.warn('No instrument found - cannot play')
      return []
    }
    const instrument = this.instruments[instrumentId]
    if (!instrument) {
      throw new Error(`Unknown instrument ID: ${instrumentId}: ${Object.keys(this.instruments)}`)
    }
    if (this.transport.state !== 'started') {
      this.transport.start()
    }

    /**
     * Schedule the noteon and noteoff events. The transport invokes the
     * callback up to the lookAhead (~100ms) BEFORE the audio deadline and
     * passes the exact AudioContext time — thread it through so instruments
     * start sample-accurately instead of "now" (GH #1501).
     */
    return events.map(({ event: midiEvent, at }) =>
      this.transport.scheduleOnce(time => {
        instrument.onEvent(midiEvent, time)
        onFire?.(midiEvent, instrument)
        this.dispatchEvent(new CustomEvent('midievent', { detail: midiEvent }))
      }, at)
    )
  }

  private setupDevices = () => {
    const audioContext = this.currentAudioContext
    if (!audioContext) {
      /** Audio stack not initialized yet - devices are set up on initialization */
      return
    }
    /** Get the input devices and set the active selected device */
    void getDevices().then(providedDevices => {
      if (audioContext.state !== 'running') {
        logger.warn('Cannot change input devices while audio context is running')
        return
      }

      const validDevices = providedDevices.filter(d => d.deviceId)

      if (validDevices.length === 0) {
        logger.warn('No audio devices found')
        return
      }

      const defaultDeviceId =
        this.defaultInputDevice && validDevices.find(d => d.deviceId === this.defaultInputDevice?.deviceId)
          ? this.defaultInputDevice.deviceId
          : validDevices[0].deviceId

      this.inputSources = validDevices.map(d => new InputSource(audioContext, d))
      this.inputDefault = this.inputSources.find(d => d.id === defaultDeviceId) ?? this.inputSources[0]

      logger.debug('Audio Devices set up', this.inputSources, this.inputDefault)

      this.routeDefaultInput(this.inputDefault)

      this.dispatchEvent(new Event('devicechange'))
    })
  }
}
